/* eslint react/prop-types: 0 */
import {Button, Card, Heading, Inline, Stack, Text, TextInput} from '@sanity/ui'
import {createRef, forwardRef, Ref, useCallback, useEffect, useId, useState} from 'react'
import {set, unset} from 'sanity'

interface ResultTypes {
  lng?: string
  lat?: string
  alt?: string
  address?: string
}

interface GenericObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

/** Request data */
const request = async (url: string) => {
  const response = await fetch(url)
  if (!response.ok) {
    const message = `An error has occured: ${response.status}`
    throw new Error(message)
  }
  const data = await response.json()
  return data
}

/** Text input component  */
const InputText = forwardRef(
  (inputProps: GenericObject, inputRef: Ref<HTMLInputElement> | undefined) => {
    const uId = useId() || ''
    const id = inputProps?.id || uId // Create an unique id
    return (
      <div>
        <label
          htmlFor={id}
          style={{
            fontSize: '.75rem',
            fontWeight: '700',
            marginBottom: '.5rem',
            display: 'inline-flex',
          }}
        >
          {inputProps?.label || ''}
        </label>
        <TextInput name={id} id={id} {...inputProps} ref={inputRef} />
      </div>
    )
  },
)
InputText.displayName = 'InputText'

/** 'Get Coordinates' component */
const CoordsSearchComponent = (props: GenericObject) => {
  const addressRef: Ref<HTMLInputElement> | undefined = createRef()
  const resultsRef: Ref<HTMLInputElement> | undefined = createRef()
  const [inputValues, setInputValues] = useState<{
    address: string | undefined
    result: string | undefined
    lng?: string | undefined
    lat?: string | undefined
  }>({address: undefined, result: undefined})

  const {schemaType, value, onChange} = props

  const handleChange = useCallback((result: ResultTypes) => (result ? set(result) : unset()), [])

  /** Handler: Get coordinates data and update the input fields */
  const handleGetCoordinates = () => {
    const addressInputValue = addressRef.current?.value
    const formattedAddress =
      addressInputValue && encodeURIComponent(addressInputValue).replaceAll('%20', '+')
    const url = `https://nominatim.openstreetmap.org/search?q=${formattedAddress}&format=geojson`

    // If empty, no address given
    if (!addressInputValue) {
      setInputValues({
        address: '',
        result: 'No address was given',
      })
      return
    }

    // If fetching
    setInputValues({
      address: '',
      result: 'Fetching...',
    })

    // Get the coordinates
    request(url)
      .then((data) => {
        const {geometry, properties} = data.features[0]

        // Set the input fields
        setInputValues({
          address: properties.display_name,
          result: `${geometry.coordinates[0]}, ${geometry.coordinates[1]}`,
        })

        // Set the data to Sanity
        onChange(
          handleChange({
            lng: geometry.coordinates[0],
            lat: geometry.coordinates[1],
            alt: undefined,
            address: properties.display_name,
          }),
        )
      })
      // If an error occurs with the request
      .catch(() => {
        setInputValues({
          address: '',
          result: 'Error! Could not find the coordinates with your query',
        })
      })
  }

  /** Handler: Clear input values and set data to Sanity */
  const handleClear = () => {
    setInputValues({address: '', result: ''})
    onChange(
      handleChange({
        lng: undefined,
        lat: undefined,
        alt: undefined,
        address: undefined,
      }),
    )
  }

  // Load input values at page load
  useEffect(() => {
    if (typeof value === 'object') {
      setInputValues({
        address: value.address,
        result: value.lat && value.lng && `${value.lat}, ${value.lng}`,
        lng: value.lng,
        lat: value.lat,
      })
    }
  }, [setInputValues, value])

  // Render
  return (
    <Card padding={[3, 3, 4]} shadow={1}>
      <Heading size={0}>{schemaType.type.title}</Heading>
      <Text muted style={{fontSize: '.85rem', marginTop: '.75rem'}}>
        {schemaType.type.description}
      </Text>

      <Stack style={{marginTop: '1rem'}}>
        <InputText
          label="Address"
          padding={[3, 3, 4]}
          placeholder={inputValues.address ? inputValues.address : 'Add address here'}
          type="text"
          ref={addressRef}
        />
      </Stack>

      <Stack style={{marginTop: '1rem'}}>
        <InputText
          label="Coordinates"
          padding={[3, 3, 4]}
          value={inputValues.result ? inputValues.result : ''}
          type="text"
          readOnly
          ref={resultsRef}
          style={{textAlign: 'center'}}
        />
        {inputValues.lat && inputValues.lng && (
          <p style={{display: 'flex', justifyContent: 'space-evenly', fontSize: '.8em'}}>
            <span>Latitude: {inputValues.lat}</span>
            <span>Longitude: {inputValues.lng}</span>
          </p>
        )}
      </Stack>

      <Inline
        style={{
          marginTop: '.5rem',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          mode="bleed"
          tone="critical"
          padding={[0]}
          style={{textAlign: 'center', cursor: 'pointer'}}
          onClick={() => handleClear()}
          type="reset"
        >
          Clear Coordinates
        </Button>
        <Button
          tone="primary"
          padding={[2, 4]}
          style={{textAlign: 'center', cursor: 'pointer'}}
          onClick={() => handleGetCoordinates()}
        >
          Get Coordinates
        </Button>
      </Inline>
    </Card>
  )
}
CoordsSearchComponent.displayName = 'CoordsSearchComponent'

export default CoordsSearchComponent
